<template>
  <div>
    <div class="hero">
      <div class="phase-pill">{{ ui.statusLabel }}</div>
      <div class="hero-ttl">{{ ui.titleTop }}<br>{{ ui.titleBottom }}</div>
      <div class="hero-sub">{{ ui.subtitleTop }}<br>{{ ui.subtitleBottom }}</div>
    </div>

    <div class="sec">
      <div class="sec-hd">参与认购</div>
      <div class="mint-card">
        <div class="mint-hdr">
          <div class="card-ttl"><span>🌕</span>{{ ui.cardTitle }}</div>
          <span class="tag tag-gold">{{ ui.priceTag }}</span>
        </div>
        <div class="mint-body">
          <div class="sale-box">
            <div class="sale-bar"><div class="sale-fill" :style="{ width: `${mintProgressWidth}%` }" /></div>
          </div>

          <div class="calc-box">
            <div class="cb-l">
              <b>{{ configInfo.showMintPrice || '500' }} {{ configInfo.usdtSymbol || 'USDT' }}</b>
            </div>
            <div class="cb-arr">→</div>
            <div class="cb-r">
              <b style="font-size: 18px; color: var(--gold-l)">{{ ui.calcLabel }}</b>
            </div>
          </div>

          <div v-if="hasMinted" class="minted-ok">✅ 已完成认购，等待预售结束后发币</div>

          <button class="main-btn btn-gold" :disabled="mintDisabled" @click="mint">{{ mintButtonText }}</button>
          <div class="main-hint" style="display: flex; align-items: center; margin-top: 10px">
            <span style="display: flex; align-items: center; gap: 5px">
              <span style="font-size: 13px">💰</span>
              <span>余额</span>
              <span style="color: var(--gold); font-family: 'Orbitron', monospace; font-weight: 700">{{ displayBalance }}</span>
            </span>
          </div>
        </div>
      </div>
    </div>

    <div class="sec">
      <div class="sec-hd">邀请推广</div>
      <div class="card">
        <div class="card-hdr">
          <div class="card-ttl"><span>🔗</span>我的邀请链接</div>
          <span class="tag tag-amber">{{ ui.inviteTag }}</span>
        </div>
        <div class="inv-body">
          <div class="link-row">
            <span class="link-txt">{{ inviteDisplayText }}</span>
            <button class="copy-btn" :disabled="copyInviteDisabled" @click="copyInvite">复制</button>
          </div>
          <div class="inv-stats inv-stats-presale">
            <div class="ist">
              <div class="ist-v">{{ userInfo.inviteNum || 0 }}</div>
              <div class="ist-l">已推荐人数</div>
            </div>
            <div class="ist">
              <div class="ist-v">{{ inviteRewardDisplay }}</div>
              <div class="ist-l">已获奖励（份）</div>
            </div>
          </div>

          <div class="invite-rule-card">
            <div class="invite-rule-head">
              <span style="font-size: 18px">🎁</span>
              <span style="font-size: 13px; font-weight: 700; color: var(--gold)">每邀请 10 人 · 送 1 份代币</span>
            </div>
            <div class="inv-flow">
              <span class="inv-pill">10人 → +1份</span>
              <span class="inv-arr">→</span>
              <span class="inv-pill">20人 → +2份</span>
              <span class="inv-arr">→</span>
              <span class="inv-pill">30人 → +3份</span>
              <span class="inv-inf">∞</span>
            </div>
            <div class="invite-rule-desc">邀请越多送的越多，无上限叠加 · 奖励代币预售结束后统一发放</div>
          </div>

          <div class="invite-progress-card">
            <div class="invite-progress-head">
              <span class="invite-progress-label">距下一份奖励</span>
              <span class="invite-progress-value">还差 {{ nextInviteGap }} 人</span>
            </div>
            <div class="invite-progress-bar">
              <div class="invite-progress-fill" :style="{ width: `${inviteProgressWidth}%` }" />
            </div>
          </div>
        </div>
      </div>
    </div>

    <div class="sec">
      <div class="notice">
        <b>⚠️ {{ ui.noticeTitle }}</b>
        <ul>
          <li>每个钱包地址仅限认购一份，重复提交合约将拒绝。</li>
          <li>预售期间代币锁定，结束后手动发放至参与地址。</li>
          <li>链上操作不可逆，请确认钱包地址后再提交。</li>
        </ul>
      </div>
    </div>
  </div>
</template>

<script>
import WalletState, { CHAIN_ID, CHAIN_ERROR_TIP } from '../state/WalletState'
import { DAPP_CONFIG } from '../config/dapp'
import { getRef } from '../utils/utils'
import { copyContent } from '../utils/utools'
import { fetchMintData, getInviteLink, mintAction } from '../services/poolService'

function createDefaultConfig() {
  return {
    usdt: '',
    usdtSymbol: 'USDT',
    showMintPrice: '500',
    mintPrice: null,
    mintQty: 0,
    mintedAmount: 0,
    pauseMint: false,
    mintInviteRewardCondtion: 10,
  }
}

function createDefaultUser() {
  return {
    showUsdtBalance: '--',
    mintNum: 0,
    inviteNum: 0,
    inviteRewardNum: 0,
    inviteIndex: 0,
    debtNum: 10,
    isActive: false,
    invitor: '',
    usdtAllowance: null,
  }
}

export default {
  name: 'MintView',
  data() {
    return {
      ui: DAPP_CONFIG.mint,
      configInfo: createDefaultConfig(),
      userInfo: createDefaultUser(),
      account: '',
      chainId: null,
      inviteLink: '',
      mintLoading: false,
      refreshDataTimer: null,
    }
  },
  computed: {
    mintProgressWidth() {
      if (!this.configInfo.mintQty) {
        return 0
      }
      return Math.min(100, (this.configInfo.mintedAmount / this.configInfo.mintQty) * 100)
    },
    displayMintQty() {
      return Number(this.configInfo.mintQty || 0).toLocaleString()
    },
    remainingShares() {
      return Math.max((this.configInfo.mintQty || 0) - (this.configInfo.mintedAmount || 0), 0)
    },
    hasMinted() {
      return (this.userInfo.mintNum || 0) > 0
    },
    mintDisabled() {
      return this.mintLoading || this.configInfo.pauseMint || this.remainingShares === 0 || this.hasMinted
    },
    mintButtonText() {
      if (this.mintLoading) return '链上确认中...'
      if (this.hasMinted) return '已认购'
      if (this.remainingShares === 0) return '已售罄'
      return '立即认购'
    },
    displayBalance() {
      return this.account ? `${this.userInfo.showUsdtBalance} ${this.configInfo.usdtSymbol || 'USDT'}` : '-- USDT'
    },
    canShowInviteLink() {
      return this.hasMinted
    },
    inviteDisplayText() {
      return (this.canShowInviteLink ? this.inviteLink : '') || this.ui.inviteHint
    },
    copyInviteDisabled() {
      return !this.canShowInviteLink || !this.inviteLink
    },
    inviteProgressWidth() {
      const total = this.configInfo.mintInviteRewardCondtion || 0
      if (!total) {
        return 0
      }
      return Math.min(100, ((this.userInfo.inviteIndex || 0) / total) * 100)
    },
    nextInviteGap() {
      const total = this.configInfo.mintInviteRewardCondtion || 0
      return Math.max(total - (this.userInfo.inviteIndex || 0), 0)
    },
    inviteRewardDisplay() {
      return this.userInfo.inviteRewardNum || '--'
    },
  },
  mounted() {
    WalletState.onStateChanged(this.handleWalletChanged)
    this.handleWalletChanged({ ...WalletState.wallet })
    this.refreshDataTimer = setInterval(() => {
      this.getInfo()
    }, 15000)
  },
  beforeDestroy() {
    clearInterval(this.refreshDataTimer)
    WalletState.removeListener(this.handleWalletChanged)
  },
  methods: {
    toast(message) {
      this.$notify({ type: 'danger', message, duration: 4000 })
    },
    toastSuccess(message) {
      this.$notify({ type: 'success', message, duration: 2500 })
    },
    showLoading(message) {
      this.$toast.loading({ duration: 0, message, forbidClick: true, loadingType: 'spinner' })
    },
    hideLoading() {
      this.$toast.clear()
    },
    async getInfo() {
      if (this.chainId !== CHAIN_ID) {
        this.userInfo = createDefaultUser()
        return
      }
      try {
        const { configInfo, userInfo } = await fetchMintData(this.account)
        this.configInfo = configInfo
        this.userInfo = { ...createDefaultUser(), ...userInfo }
      } catch (error) {
        this.toast(error.message)
      }
    },
    handleWalletChanged(wallet) {
      this.chainId = wallet.chainId
      this.account = wallet.account || ''
      this.inviteLink = this.account ? getInviteLink(this.account) : ''
      this.getInfo()
    },
    async mint() {
      let wallet = null
      try {
        wallet = await WalletState.ensureWallet(!this.account)
      } catch (error) {
        this.toast(error.message || '\u8fde\u63a5\u94b1\u5305\u5931\u8d25')
        return
      }
      this.chainId = wallet.chainId
      this.account = wallet.account || ''
      this.inviteLink = this.account ? getInviteLink(this.account) : ''
      if (!this.account) {
        return
      }
      if (Number(this.chainId) !== Number(CHAIN_ID)) {
        this.toast(CHAIN_ERROR_TIP)
        return
      }
      if (this.mintDisabled) {
        return
      }
      if (!this.configInfo.usdt) {
        this.toast('请等待获取认购信息')
        return
      }
      this.showLoading('认购中...')
      this.mintLoading = true
      try {
        const transaction = await mintAction({
          account: this.account,
          configInfo: this.configInfo,
          userInfo: this.userInfo,
          invitor: getRef(),
        })
        if (transaction.status) {
          this.toastSuccess('认购成功')
          await this.getInfo()
        } else {
          this.toast('认购失败')
        }
      } catch (error) {
        this.toast(error.message)
      } finally {
        this.hideLoading()
        this.mintLoading = false
      }
    },
    async copyInvite() {
      if (this.copyInviteDisabled) {
        this.toast(this.ui.inviteHint)
        return
      }
      try {
        await copyContent(this.inviteLink)
      } catch (error) {
        // utility handles feedback
      }
    },
  },
}
</script>
